import React, { useState, useEffect } from 'react';
import { api } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import { formatDate } from '../lib/utils';
import { useFeatures } from '../lib/features';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Plus, 
  Calendar,
  Lock,
  Unlock,
  MoreVertical,
  Trash2,
  Edit,
  LayoutGrid,
  X,
  Copy,
  FileUp,
  RefreshCw
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Link } from 'react-router-dom';

const formatMoney = (value) =>
  `$${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const defaultColors = [
  '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444', 
  '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1'
];

const EditionsPage = () => {
  const { user } = useAuth();
  const isReadOnly = user?.role === 'sales_rep';
  const [editions, setEditions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [copyAdsTarget, setCopyAdsTarget] = useState(null);
  const [selectedEdition, setSelectedEdition] = useState(null);
  const [reprocessingId, setReprocessingId] = useState(null);

  // Per-card hidden file input ref-list for the "Re-upload PDF" button
  const handleReuploadPdf = async (editionId, file) => {
    if (!file) return;
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      alert('Please choose a PDF file.');
      return;
    }
    setReprocessingId(editionId);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/editions/${editionId}/process-pdf`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      alert('PDF uploaded — page overlays are being regenerated. The Flatplan will pick them up automatically.');
      loadEditions();
    } catch (err) {
      alert('Re-upload failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setReprocessingId(null);
    }
  };

  useEffect(() => {
    loadEditions();
  }, []);

  const loadEditions = async () => {
    try {
      const data = await api.get('/editions');
      setEditions(data);
    } catch (error) {
      console.error('Failed to load editions:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleLock = async (edition) => {
    try {
      await api.put(`/editions/${edition.id}`, { is_locked: !edition.is_locked });
      loadEditions();
    } catch (error) {
      console.error('Failed to toggle lock:', error);
    }
  };

  const deleteEdition = async (editionId) => {
    if (!window.confirm('Delete this edition? All associated bookings will need to be reassigned.')) return;
    try {
      await api.delete(`/editions/${editionId}`);
      loadEditions();
    } catch (error) {
      console.error('Failed to delete edition:', error);
    }
  };

  return (
    <div className="space-y-6" data-testid="editions-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#F8FAFC] tracking-tight">Editions</h1>
          <p className="text-[#94A3B8] mt-1">Manage newspaper editions and publishing schedule</p>
        </div>
        <div className="flex gap-2">
          {!isReadOnly && (
            <>
              <Button
                onClick={() => setShowBulkModal(true)}
                data-testid="bulk-create-editions-btn"
                variant="outline"
                className="border-[#1E293B] bg-[#121212] text-[#F8FAFC] hover:bg-[#1E293B]"
              >
                <Copy className="w-4 h-4 mr-2" />
                Bulk Create
              </Button>
              <Button 
                onClick={() => setShowCreateModal(true)}
                data-testid="create-edition-btn"
                className="bg-[#3B82F6] hover:bg-[#2563EB] text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Edition
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Editions Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3B82F6]"></div>
        </div>
      ) : editions.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {editions.map((edition, idx) => (
            <div 
              key={edition.id}
              className="bg-[#121212] border border-[#1E293B] rounded-md p-4 hover:border-[#3B82F6]/50 transition-colors animate-fadeInUp"
              style={{ animationDelay: `${idx * 50}ms` }}
              data-testid={`edition-card-${edition.id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-md bg-[#1E293B] flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-[#3B82F6]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#F8FAFC]">{edition.name}</h3>
                    <p className="text-xs text-[#94A3B8]">{formatDate(edition.publish_date)}</p>
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`edition-menu-${edition.id}`}>
                      <MoreVertical className="h-4 w-4 text-[#94A3B8]" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-[#121212] border-[#1E293B]">
                    <DropdownMenuItem asChild className="text-[#F8FAFC] focus:bg-[#1E293B]">
                      <Link to={`/flatplan/${edition.id}`}>
                        <LayoutGrid className="w-4 h-4 mr-2" /> Open Flatplan
                      </Link>
                    </DropdownMenuItem>
                    {!isReadOnly && (
                      <>
                        <DropdownMenuItem 
                          onClick={() => {
                            setSelectedEdition(edition);
                            setShowCreateModal(true);
                          }}
                          className="text-[#F8FAFC] focus:bg-[#1E293B]"
                        >
                          <Edit className="w-4 h-4 mr-2" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => setCopyAdsTarget(edition)}
                          className="text-[#F8FAFC] focus:bg-[#1E293B]"
                          data-testid={`copy-ads-from-${edition.id}`}
                        >
                          <Copy className="w-4 h-4 mr-2" /> Copy Ads From…
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => toggleLock(edition)}
                          className="text-[#F8FAFC] focus:bg-[#1E293B]"
                        >
                          {edition.is_locked ? (
                            <><Unlock className="w-4 h-4 mr-2" /> Unlock</>
                          ) : (
                            <><Lock className="w-4 h-4 mr-2" /> Lock Edition</>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => deleteEdition(edition.id)}
                          className="text-red-400 focus:bg-[#1E293B]"
                        >
                          <Trash2 className="w-4 h-4 mr-2" /> Delete
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Sections preview */}
              <div className="flex flex-wrap gap-1 mb-4">
                {edition.sections?.map((section, i) => (
                  <span 
                    key={i}
                    className="px-2 py-0.5 text-xs rounded"
                    style={{ backgroundColor: `${section.color}20`, color: section.color }}
                  >
                    {section.name} (p.{section.start_page}-{section.end_page})
                  </span>
                ))}
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-[#1E293B]">
                <div className="flex items-center gap-2">
                  {edition.is_locked ? (
                    <span className="flex items-center gap-1 text-xs text-yellow-400">
                      <Lock className="w-3 h-3" /> Locked
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-xs text-emerald-400">
                      <Unlock className="w-3 h-3" /> Open
                    </span>
                  )}
                </div>
                <p className="text-sm text-[#94A3B8]">{edition.page_count} pages</p>
              </div>

              {/* Sales + paid + print run + cost (shown if any value is available) */}
              {(edition.copies_ordered != null
                || edition.printing_cost != null
                || (edition.total_sales || 0) > 0
                || (edition.total_paid || 0) > 0) && (
                <div className="grid grid-cols-2 gap-2 text-xs text-[#94A3B8] pt-2 border-t border-[#1E293B]" data-testid={`edition-print-info-${edition.id}`}>
                  {/* Left column: Sales + Copies + Sales per page */}
                  <div className="space-y-1">
                    <div data-testid={`edition-total-sales-${edition.id}`}>
                      <span className="text-[#475569] uppercase tracking-wide text-[10px]">Sales </span>
                      <span className="text-[#10B981] font-mono">{formatMoney(edition.total_sales)}</span>
                    </div>
                    {edition.copies_ordered != null && (
                      <div data-testid={`edition-copies-${edition.id}`}>
                        <span className="text-[#475569] uppercase tracking-wide text-[10px]">Copies </span>
                        <span className="text-[#F8FAFC] font-mono">{Number(edition.copies_ordered).toLocaleString()}</span>
                      </div>
                    )}
                    {(edition.total_sales || 0) > 0 && edition.page_count > 0 && (
                      <div data-testid={`edition-sales-per-page-${edition.id}`}>
                        <span className="text-[#475569] uppercase tracking-wide text-[10px]">Sales / pg </span>
                        <span className="text-[#10B981] font-mono">
                          ${(edition.total_sales / edition.page_count).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    )}
                  </div>
                  {/* Right column: Paid + Print cost + Cost per page */}
                  <div className="space-y-1 text-right">
                    <div data-testid={`edition-total-paid-${edition.id}`}>
                      <span className="text-[#475569] uppercase tracking-wide text-[10px]">Paid </span>
                      <span className="text-[#38BDF8] font-mono">{formatMoney(edition.total_paid)}</span>
                    </div>
                    {edition.printing_cost != null && (
                      <div data-testid={`edition-printing-cost-${edition.id}`}>
                        <span className="text-[#475569] uppercase tracking-wide text-[10px]">Print cost </span>
                        <span className="text-[#F8FAFC] font-mono">${Number(edition.printing_cost).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                    )}
                    {edition.printing_cost != null && edition.page_count > 0 && (
                      <div data-testid={`edition-cost-per-page-${edition.id}`}>
                        <span className="text-[#475569] uppercase tracking-wide text-[10px]">Cost / pg </span>
                        <span className="text-[#F59E0B] font-mono">
                          ${(Number(edition.printing_cost) / edition.page_count).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Open Flatplan + Re-upload PDF buttons */}
              <div className="flex gap-2 mt-3">
                <Link to={`/flatplan/${edition.id}`} className="flex-1">
                  <Button 
                    variant="outline" 
                    className="w-full border-[#1E293B] text-[#94A3B8] hover:bg-[#1E293B] hover:text-[#F8FAFC]"
                    data-testid={`open-flatplan-${edition.id}`}
                  >
                    <LayoutGrid className="w-4 h-4 mr-2" />
                    Open Flatplan
                  </Button>
                </Link>
                {!isReadOnly && (
                  <>
                    <Button
                      variant="outline"
                      size="icon"
                      className="border-[#1E293B] text-[#94A3B8] hover:bg-[#1E293B] hover:text-[#F8FAFC] shrink-0"
                      title={(edition.overlay_page_images && edition.overlay_page_images.length > 0)
                        ? 'Re-upload edition PDF (regenerates page overlays)'
                        : 'Upload edition PDF (generates page overlays)'}
                      onClick={() => document.getElementById(`reupload-pdf-${edition.id}`)?.click()}
                      disabled={reprocessingId === edition.id}
                      data-testid={`reupload-pdf-btn-${edition.id}`}
                    >
                      {reprocessingId === edition.id
                        ? <RefreshCw className="w-4 h-4 animate-spin" />
                        : <FileUp className="w-4 h-4" />}
                    </Button>
                    <input
                      id={`reupload-pdf-${edition.id}`}
                      type="file"
                      accept="application/pdf"
                      className="hidden"
                      onChange={(e) => {
                        handleReuploadPdf(edition.id, e.target.files?.[0]);
                        e.target.value = '';
                      }}
                    />
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center h-64 text-[#94A3B8]">
          <Calendar className="w-16 h-16 mb-4 opacity-50" />
          <p className="text-lg">No editions found</p>
          {!isReadOnly && (
            <Button 
              onClick={() => setShowCreateModal(true)} 
              variant="outline" 
              className="mt-4 border-[#1E293B] text-[#94A3B8] hover:bg-[#1E293B]"
            >
              Create your first edition
            </Button>
          )}
        </div>
      )}

      {/* Create/Edit Edition Modal */}
      <EditionModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setSelectedEdition(null);
        }}
        edition={selectedEdition}
        onSave={() => {
          loadEditions();
          setShowCreateModal(false);
          setSelectedEdition(null);
        }}
      />

      {/* Bulk Create Editions Modal */}
      <BulkEditionsModal
        isOpen={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        editions={editions}
        onSave={() => { loadEditions(); setShowBulkModal(false); }}
      />

      {/* Copy Ads From Modal */}
      <CopyAdsFromModal
        target={copyAdsTarget}
        editions={editions}
        onClose={() => setCopyAdsTarget(null)}
        onSaved={() => { setCopyAdsTarget(null); loadEditions(); }}
      />
    </div>
  );
};

// Bulk Create Editions Modal
const BulkEditionsModal = ({ isOpen, onClose, editions, onSave }) => {
  const [baseEditionId, setBaseEditionId] = useState('');
  const [count, setCount] = useState(12);
  const [periodDays, setPeriodDays] = useState(14);
  const [startDate, setStartDate] = useState('');
  const [namePattern, setNamePattern] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setBaseEditionId(editions?.[0]?.id || '');
      setCount(12);
      setPeriodDays(14);
      setStartDate('');
      setNamePattern('');
    }
  }, [isOpen, editions]);

  const baseEdition = editions.find(e => e.id === baseEditionId);

  // Build preview of first 3 editions
  const preview = [];
  if (baseEdition && startDate && count > 0) {
    const m = (baseEdition.name || '').match(/(\d+)\s*$/);
    const isYearLike = m && +m[1] >= 1900 && +m[1] <= 2100;
    const useIncrement = m && !isYearLike;
    const baseNumber = useIncrement ? parseInt(m[1], 10) : 0;
    const namePrefix = useIncrement ? baseEdition.name.slice(0, m.index) : null;
    const start = new Date(startDate + 'T00:00:00');
    for (let i = 0; i < Math.min(count, 3); i++) {
      const d = new Date(start); d.setDate(d.getDate() + periodDays * i);
      let nm;
      if (namePattern) {
        nm = namePattern
          .replace('{date}', d.toISOString().slice(0, 10))
          .replace('{n}', String(i + 1))
          .replace('{base}', baseEdition.name);
      } else if (useIncrement) {
        nm = `${namePrefix}${baseNumber + i + 1}`;
      } else {
        nm = `Edition ${d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })}`;
      }
      preview.push({ name: nm, date: d.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }) });
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!baseEditionId || !startDate || count < 1) return;
    setSubmitting(true);
    try {
      await api.post('/editions/bulk', {
        base_edition_id: baseEditionId,
        count: Number(count),
        period_days: Number(periodDays),
        start_date: startDate,
        ...(namePattern ? { name_pattern: namePattern } : {}),
      });
      onSave();
    } catch (err) {
      alert('Bulk create failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#121212] border-[#1E293B] text-[#F8FAFC] max-w-xl" data-testid="bulk-editions-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Copy className="w-5 h-5 text-[#3B82F6]" /> Bulk Create Editions</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-[#94A3B8]">Base Edition (copies sections & page count)</Label>
            <select
              value={baseEditionId}
              onChange={(e) => setBaseEditionId(e.target.value)}
              className="w-full bg-[#0A0A0A] border border-[#1E293B] text-[#F8FAFC] rounded-md h-10 px-3 text-sm"
              required
              data-testid="bulk-base-edition-select"
            >
              <option value="">Select base edition…</option>
              {[...editions]
                .sort((a, b) => (b.publish_date || '').localeCompare(a.publish_date || ''))
                .map(ed => (
                  <option key={ed.id} value={ed.id}>{ed.name} — {ed.publish_date}</option>
                ))}
            </select>
            {baseEdition && (
              <p className="text-xs text-[#475569]">
                Will clone {baseEdition.sections?.length || 0} sections · {baseEdition.page_count} pages
              </p>
            )}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Number of Issues</Label>
              <Input
                type="number" min="1" max="52" value={count}
                onChange={(e) => setCount(e.target.value)}
                className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]"
                required
                data-testid="bulk-count-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Days Between</Label>
              <Input
                type="number" min="1" value={periodDays}
                onChange={(e) => setPeriodDays(e.target.value)}
                className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]"
                required
                data-testid="bulk-period-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Start Date</Label>
              <Input
                type="date" value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]"
                required
                data-testid="bulk-start-date-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-[#94A3B8]">Name Pattern (optional)</Label>
            <Input
              placeholder="e.g. VOL. 32 ED. {n}   — leave blank to auto-increment base name"
              value={namePattern}
              onChange={(e) => setNamePattern(e.target.value)}
              className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]"
              data-testid="bulk-name-pattern-input"
            />
            <p className="text-xs text-[#475569]">Tokens: {'{n}'}, {'{date}'}, {'{base}'}</p>
          </div>

          {preview.length > 0 && (
            <div className="bg-[#0A0A0A] border border-[#1E293B] rounded-md p-3 space-y-1" data-testid="bulk-preview">
              <p className="text-xs uppercase tracking-wide text-[#475569]">Preview · first {preview.length} of {count}</p>
              {preview.map((p) => (
                <div key={`${p.name}-${p.date}`} className="text-sm text-[#F8FAFC] flex justify-between">
                  <span>{p.name}</span>
                  <span className="text-[#94A3B8]">{p.date}</span>
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="border-[#1E293B] bg-[#121212] text-[#F8FAFC]">
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || !baseEditionId || !startDate} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="bulk-submit-btn">
              {submitting ? 'Creating…' : `Create ${count} Edition${count !== 1 ? 's' : ''}`}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Edition Modal Component with Section Management
const EditionModal = ({ isOpen, onClose, edition, onSave }) => {
  const { multiCurrency } = useFeatures();
  const [formData, setFormData] = useState({
    name: '',
    publish_date: '',
    page_count: 24,
    exchange_rate: '',
    copies_ordered: '',
    printing_cost: '',
    sections: []
  });
  const [saving, setSaving] = useState(false);
  const [fetchingRate, setFetchingRate] = useState(false);

  useEffect(() => {
    if (edition) {
      setFormData({
        name: edition.name || '',
        publish_date: edition.publish_date || '',
        page_count: edition.page_count || 24,
        exchange_rate: edition.exchange_rate || '',
        copies_ordered: edition.copies_ordered ?? '',
        printing_cost: edition.printing_cost ?? '',
        sections: edition.sections?.map(s => ({ ...s, _key: s._key || Math.random().toString(36).slice(2) })) || []
      });
    } else {
      // Default to next week with default sections
      const nextWeek = new Date();
      nextWeek.setDate(nextWeek.getDate() + 7);
      setFormData({
        name: '',
        publish_date: nextWeek.toISOString().split('T')[0],
        page_count: 24,
        exchange_rate: '',
        copies_ordered: '',
        printing_cost: '',
        sections: [
          { _key: Math.random().toString(36).slice(2), name: 'Front', start_page: 1, end_page: 4, color: '#3B82F6' },
          { _key: Math.random().toString(36).slice(2), name: 'News', start_page: 5, end_page: 12, color: '#10B981' },
          { _key: Math.random().toString(36).slice(2), name: 'Sports', start_page: 13, end_page: 18, color: '#F59E0B' },
          { _key: Math.random().toString(36).slice(2), name: 'Classifieds', start_page: 19, end_page: 24, color: '#8B5CF6' }
        ]
      });
    }
  }, [edition]);

  const fetchExchangeRate = async () => {
    if (!formData.publish_date) { alert('Set publish date first'); return; }
    const pubDate = new Date(formData.publish_date + 'T00:00:00');
    if (pubDate > new Date()) { alert('Cannot fetch exchange rate for future dates'); return; }
    setFetchingRate(true);
    try {
      const data = await api.get(`/exchange-rate?date=${formData.publish_date}`);
      if (data.rate) {
        setFormData(prev => ({ ...prev, exchange_rate: data.rate }));
      } else {
        alert(data.error || 'Exchange rate not found for this date');
      }
    } catch (err) { alert('Failed to fetch exchange rate'); }
    finally { setFetchingRate(false); }
  };

  const addSection = () => {
    const lastSection = formData.sections[formData.sections.length - 1];
    const startPage = lastSection ? lastSection.end_page + 1 : 1;
    const colorIndex = formData.sections.length % defaultColors.length;
    setFormData({
      ...formData,
      sections: [
        ...formData.sections,
        { 
          name: 'New Section', 
          start_page: startPage, 
          end_page: Math.min(startPage + 3, formData.page_count), 
          color: defaultColors[colorIndex] 
        }
      ]
    });
  };

  const updateSection = (index, field, value) => {
    const newSections = [...formData.sections];
    newSections[index] = { ...newSections[index], [field]: value };
    setFormData({ ...formData, sections: newSections });
  };

  const removeSection = (index) => {
    const newSections = formData.sections.filter((_, i) => i !== index);
    setFormData({ ...formData, sections: newSections });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Strip UI-only fields and normalize empty strings to null for numeric fields
      const cleanSections = (formData.sections || []).map(({ _key, ...rest }) => rest);
      const payload = {
        name: formData.name,
        publish_date: formData.publish_date,
        page_count: formData.page_count,
        sections: cleanSections,
        exchange_rate: formData.exchange_rate === '' || formData.exchange_rate == null
          ? null
          : Number(formData.exchange_rate),
        copies_ordered: formData.copies_ordered === '' || formData.copies_ordered == null
          ? null
          : parseInt(formData.copies_ordered, 10),
        printing_cost: formData.printing_cost === '' || formData.printing_cost == null
          ? null
          : Number(formData.printing_cost),
      };
      if (edition) {
        await api.put(`/editions/${edition.id}`, payload);
      } else {
        await api.post('/editions', payload);
      }
      onSave();
    } catch (error) {
      console.error('Failed to save edition:', error);
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map(d => `${(d.loc || []).join('.')}: ${d.msg}`).join('\n')
        : (detail || error.message || 'Unknown error');
      alert('Failed to save edition:\n' + msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#121212] border-[#1E293B] text-[#F8FAFC] max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{edition ? 'Edit Edition' : 'New Edition'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Edition Name *</Label>
              <Input
                data-testid="edition-name-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Weekly Edition - January 2026"
                required
                className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#94A3B8]">Publish Date *</Label>
                <Input
                  data-testid="edition-date-input"
                  type="date"
                  value={formData.publish_date}
                  onChange={(e) => setFormData({ ...formData, publish_date: e.target.value })}
                  required
                  className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#94A3B8]">Page Count</Label>
                <Input
                  data-testid="edition-pages-input"
                  type="number"
                  min="4"
                  max="100"
                  value={formData.page_count}
                  onChange={(e) => setFormData({ ...formData, page_count: parseInt(e.target.value) || 24 })}
                  className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]"
                />
              </div>
            </div>
            {multiCurrency && (
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Exchange Rate (USD/MXN)</Label>
              <div className="flex gap-2">
                <Input
                  data-testid="edition-exchange-rate-input"
                  type="number"
                  step="0.0001"
                  min="0"
                  value={formData.exchange_rate}
                  onChange={(e) => setFormData({ ...formData, exchange_rate: parseFloat(e.target.value) || '' })}
                  placeholder="e.g., 17.2500"
                  className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={fetchExchangeRate}
                  disabled={fetchingRate || !formData.publish_date}
                  className="border-[#1E293B] text-[#94A3B8] whitespace-nowrap"
                  data-testid="fetch-rate-btn"
                >
                  {fetchingRate ? 'Fetching...' : 'Get from DOF'}
                </Button>
              </div>
            </div>
            )}
          </div>

          {/* Print run + cost */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Copies Ordered</Label>
              <Input
                data-testid="edition-copies-ordered-input"
                type="number"
                min="0"
                step="1"
                value={formData.copies_ordered}
                onChange={(e) => setFormData({ ...formData, copies_ordered: e.target.value })}
                placeholder="e.g., 5000"
                className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Printing Cost</Label>
              <Input
                data-testid="edition-printing-cost-input"
                type="number"
                min="0"
                step="0.01"
                value={formData.printing_cost}
                onChange={(e) => setFormData({ ...formData, printing_cost: e.target.value })}
                placeholder="e.g., 1250.00"
                className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]"
              />
            </div>
          </div>

          {/* Sections Management */}
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b border-[#1E293B] pb-2">
              <h3 className="text-sm font-medium text-[#94A3B8] uppercase tracking-wide">
                Sections
              </h3>
              <Button 
                type="button" 
                variant="outline" 
                size="sm"
                onClick={addSection}
                className="border-[#1E293B] text-[#94A3B8] hover:bg-[#1E293B]"
                data-testid="add-section-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Section
              </Button>
            </div>

            <div className="space-y-3">
              {formData.sections.map((section, index) => (
                <div 
                  key={section._key || `section-${index}`} 
                  className="flex items-center gap-3 p-3 bg-[#0A0A0A] border border-[#1E293B] rounded-md"
                  data-testid={`section-${index}`}
                >
                  {/* Color picker */}
                  <input
                    type="color"
                    value={section.color}
                    onChange={(e) => updateSection(index, 'color', e.target.value)}
                    className="w-8 h-8 rounded cursor-pointer bg-transparent border-0"
                    title="Section color"
                  />
                  
                  {/* Section name */}
                  <Input
                    value={section.name}
                    onChange={(e) => updateSection(index, 'name', e.target.value)}
                    placeholder="Section name"
                    className="bg-[#121212] border-[#1E293B] text-[#F8FAFC] flex-1"
                    data-testid={`section-name-${index}`}
                  />
                  
                  {/* Page range */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[#94A3B8]">Pages</span>
                    <Input
                      type="number"
                      min="1"
                      max={formData.page_count}
                      value={section.start_page}
                      onChange={(e) => updateSection(index, 'start_page', parseInt(e.target.value) || 1)}
                      className="bg-[#121212] border-[#1E293B] text-[#F8FAFC] w-16 text-center"
                      data-testid={`section-start-${index}`}
                    />
                    <span className="text-[#94A3B8]">to</span>
                    <Input
                      type="number"
                      min="1"
                      max={formData.page_count}
                      value={section.end_page}
                      onChange={(e) => updateSection(index, 'end_page', parseInt(e.target.value) || 1)}
                      className="bg-[#121212] border-[#1E293B] text-[#F8FAFC] w-16 text-center"
                      data-testid={`section-end-${index}`}
                    />
                  </div>

                  {/* Remove button */}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeSection(index)}
                    className="text-red-400 hover:text-red-300 hover:bg-red-900/20 p-2"
                    data-testid={`remove-section-${index}`}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}

              {formData.sections.length === 0 && (
                <div className="text-center py-6 text-[#94A3B8]">
                  <p className="text-sm">No sections defined</p>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={addSection}
                    className="mt-2 border-[#1E293B] text-[#94A3B8]"
                  >
                    Add your first section
                  </Button>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-[#1E293B]">
            <Button type="button" variant="outline" onClick={onClose} className="border-[#1E293B] text-[#94A3B8]">
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={saving}
              data-testid="save-edition-btn"
              className="bg-[#3B82F6] hover:bg-[#2563EB] text-white"
            >
              {saving ? 'Saving...' : (edition ? 'Update' : 'Create')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Copy Ads From Modal
const CopyAdsFromModal = ({ target, editions, onClose, onSaved }) => {
  const [sourceId, setSourceId] = useState('');
  const [bookings, setBookings] = useState([]);
  const [targetAdvertiserIds, setTargetAdvertiserIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [filter, setFilter] = useState('');
  const [skipDuplicates, setSkipDuplicates] = useState(true);

  useEffect(() => {
    if (target) {
      // Default source = immediate previous edition (most recent edition with publish_date < target's)
      let defaultSourceId = '';
      if (target.publish_date) {
        const prior = [...editions]
          .filter(e => e.id !== target.id && e.publish_date && e.publish_date < target.publish_date)
          .sort((a, b) => (b.publish_date || '').localeCompare(a.publish_date || ''));
        defaultSourceId = prior[0]?.id || '';
      }
      setSourceId(defaultSourceId);
      setBookings([]);
      setSelectedIds([]);
      setFilter('');
      setSkipDuplicates(true);

      // Load advertiser IDs already in target so we can flag duplicates
      api.get(`/bookings?edition_id=${target.id}`)
        .then(d => setTargetAdvertiserIds(new Set(d.map(b => b.advertiser_id).filter(Boolean))))
        .catch(() => setTargetAdvertiserIds(new Set()));
    }
  }, [target, editions]);

  useEffect(() => {
    if (!sourceId) { setBookings([]); setSelectedIds([]); return; }
    const load = async () => {
      setLoading(true);
      try {
        const data = await api.get(`/bookings?edition_id=${sourceId}`);
        setBookings(data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  }, [sourceId]);

  // Recompute pre-selected IDs whenever the source data or skipDuplicates toggle changes
  useEffect(() => {
    const eligible = skipDuplicates
      ? bookings.filter(b => !targetAdvertiserIds.has(b.advertiser_id))
      : bookings;
    setSelectedIds(eligible.map(b => b.id));
  }, [bookings, skipDuplicates, targetAdvertiserIds]);

  const toggle = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleAll = () => setSelectedIds(prev => prev.length === filtered.length ? [] : filtered.map(b => b.id));

  const filtered = bookings.filter(b =>
    !filter || `${b.advertiser_name || ''} ${b.product_type || ''}`.toLowerCase().includes(filter.toLowerCase())
  );

  const handleSubmit = async () => {
    if (!sourceId || selectedIds.length === 0) return;
    setSubmitting(true);
    try {
      const res = await api.post(`/editions/${target.id}/copy-ads-from`, {
        source_edition_id: sourceId,
        booking_ids: selectedIds,
      });
      alert(`Copied ${res.created} ad${res.created !== 1 ? 's' : ''} into ${target.name}.`);
      onSaved();
    } catch (err) {
      alert('Copy failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  if (!target) return null;
  const sourceOptions = [...editions]
    .filter(e => e.id !== target.id)
    .sort((a, b) => (b.publish_date || '').localeCompare(a.publish_date || ''));

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121212] border-[#1E293B] text-[#F8FAFC] max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="copy-ads-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy className="w-5 h-5 text-[#3B82F6]" /> Copy Ads Into {target.name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-[#94A3B8]">Source Edition</Label>
            <select
              value={sourceId}
              onChange={(e) => setSourceId(e.target.value)}
              className="w-full bg-[#0A0A0A] border border-[#1E293B] text-[#F8FAFC] rounded-md h-10 px-3 text-sm"
              data-testid="copy-ads-source-select"
            >
              <option value="">Choose an edition to copy ads from…</option>
              {sourceOptions.map(ed => (
                <option key={ed.id} value={ed.id}>{ed.name} — {ed.publish_date}</option>
              ))}
            </select>
            <p className="text-xs text-[#475569]">Each ad's page placement (page number + position) will be copied along with the ad itself.</p>
          </div>

          {sourceId && (
            <>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <Input
                  placeholder="Filter by advertiser or product…"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC] flex-1 min-w-[200px]"
                  data-testid="copy-ads-filter"
                />
                <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="copy-ads-skip-duplicates-label">
                  <input
                    type="checkbox"
                    checked={skipDuplicates}
                    onChange={(e) => setSkipDuplicates(e.target.checked)}
                    className="h-4 w-4 accent-[#3B82F6]"
                    data-testid="copy-ads-skip-duplicates"
                  />
                  <span className="text-xs text-[#94A3B8] whitespace-nowrap">Skip if advertiser already has an ad in target</span>
                </label>
              </div>
              <div className="flex items-center justify-between text-xs text-[#94A3B8]">
                <span>{selectedIds.length} of {bookings.length} selected{filter ? ` (${filtered.length} match filter)` : ''}</span>
                <button onClick={toggleAll} className="text-[#3B82F6] hover:underline" data-testid="copy-ads-toggle-all">
                  {filtered.every(b => selectedIds.includes(b.id)) ? 'Deselect all' : 'Select all'}
                </button>
              </div>

              <div className="max-h-72 overflow-y-auto border border-[#1E293B] rounded-md divide-y divide-[#1E293B]" data-testid="copy-ads-list">
                {loading ? (
                  <p className="p-4 text-sm text-[#475569] text-center">Loading…</p>
                ) : filtered.length === 0 ? (
                  <p className="p-4 text-sm text-[#475569] text-center">No bookings in that edition</p>
                ) : (
                  filtered.map(b => {
                    const isDup = targetAdvertiserIds.has(b.advertiser_id);
                    return (
                      <label
                        key={b.id}
                        className={`flex items-center gap-3 px-3 py-2 hover:bg-[#1E293B] cursor-pointer text-sm ${isDup ? 'opacity-70' : ''}`}
                        data-testid={`copy-ads-row-${b.id}`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(b.id)}
                          onChange={() => toggle(b.id)}
                          className="h-4 w-4 accent-[#3B82F6]"
                        />
                        <span className="flex-1 text-[#F8FAFC]">
                          {b.advertiser_name || '—'}
                          {isDup && (
                            <span className="ml-2 text-[10px] uppercase tracking-wide text-amber-400 bg-amber-900/30 border border-amber-700/40 rounded px-1.5 py-0.5" data-testid={`copy-ads-dup-${b.id}`}>
                              already in target
                            </span>
                          )}
                        </span>
                        <span className="text-[#94A3B8] text-xs">{b.product_type}</span>
                        <span className="text-[#3B82F6] text-xs w-12 text-right">
                          {b.page_number ? `p.${b.page_number}` : '—'}
                        </span>
                        <span className="text-emerald-400 text-xs w-16 text-right">${(b.rate || 0).toFixed(0)}</span>
                      </label>
                    );
                  })
                )}
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={onClose} className="border-[#1E293B] bg-[#121212] text-[#F8FAFC]">Cancel</Button>
                <Button
                  type="button"
                  disabled={submitting || selectedIds.length === 0}
                  onClick={handleSubmit}
                  className="bg-[#3B82F6] hover:bg-[#2563EB] text-white"
                  data-testid="copy-ads-submit"
                >
                  {submitting ? 'Copying…' : `Copy ${selectedIds.length} Ad${selectedIds.length !== 1 ? 's' : ''}`}
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditionsPage;
